import React, { useCallback, useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import Image from 'next/image'
import cx from 'classnames'
import styles from './Modal.module.scss'
import { ModalProps } from '~types'
import { disableBodyScroll, getPortalRoot } from 'utils'
import { NEXT_IMAGE_QUALITY } from '~constants'
import { useWindowSize } from 'utils/hooks'
import { useModalExists } from 'lib/context/modalContext'
import ModalViewFormSuccess from './views/ModalViewFormSuccess/ModalViewFormSuccess'
import ModalViewEventAnnouncement from './views/ModalViewEventAccouncement/ModalViewEventAccouncement'
import ModalViewMedia from './views/ModalViewMedia/ModalViewMedia'
import CloseThick from '/public/assets/svg/close-thick.svg'
import Loader from 'components/modules/Calendar/views/CalendarMultiArtist/elements/Loader/Loader'

const Modal = ({
  backgroundColor = 'black',
  overlayTheme = 'dark',
  backgroundImageXl,
  backgroundImageSm,
  view,
  children,
  closeOnOutsideClick = true,
  handleModalClose,
  hasBorder = false,
  isOpen = false,
  width
}: ModalProps) => {
  const { isBreakpoint: isBelowSm } = useWindowSize('sm', '<')
  const backgroundImage = isBelowSm ? backgroundImageSm ?? backgroundImageXl : backgroundImageXl
  const contentRef = useRef<HTMLDivElement>(null)
  const [isMediaLoaded, setIsMediaLoaded] = useState(false)
  const hasBackgroundVideo = view?.componentName === 'ModalViewMedia' && view?.mediaVideo
  const { openModal, closeModal } = useModalExists()

  useEffect(() => {
    disableBodyScroll(isOpen)
    if (!isOpen) setIsMediaLoaded(false)
    return () => disableBodyScroll(false)
  }, [isOpen])

  //Tracks this modal's open/close state in the global modal registry
  // to coordinate media playback across the entire application.
  useEffect(() => {
    if (isOpen) {
      openModal()
    }
    return () => {
      if (isOpen) {
        closeModal()
      }
    }
  }, [isOpen, openModal, closeModal])

  const getView = () => {
    switch (view?.componentName) {
      case 'ModalViewFormSuccess':
        return <ModalViewFormSuccess {...view} isBelowSm={isBelowSm} handleModalClose={handleModalClose} />
      case 'ModalViewEventAnnouncement':
        return <ModalViewEventAnnouncement {...view} />
      case 'ModalViewMedia':
        return (
          <>
            {!isMediaLoaded && (
              <div className={styles['loader-container']}>
                <Loader />
              </div>
            )}
            <ModalViewMedia {...view} onMediaLoad={() => setIsMediaLoaded(true)} />
          </>
        )
      default:
        return null
    }
  }

  // Handle Escape key to close modal
  const handleKeyEvent = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen && handleModalClose) {
        handleModalClose()
      }
    },
    [isOpen, handleModalClose]
  )

  useEffect(() => {
    window.addEventListener('keydown', handleKeyEvent)
    return () => window.removeEventListener('keydown', handleKeyEvent)
  }, [handleKeyEvent])

  // Handle clicking outside `modal-content`
  const onModalOutsideClick = (e: React.MouseEvent<HTMLElement>) => {
    if (!closeOnOutsideClick || !handleModalClose) return

    if (contentRef.current && !contentRef.current.contains(e.target as Node)) {
      handleModalClose()
      disableBodyScroll(false)
    }
  }

  const modalContent = isOpen ? (
    <div
      className={cx([styles['modal'], styles[`modal--overlay-theme-${overlayTheme}`]])}
      role='dialog'
      aria-modal='true'
      onClick={onModalOutsideClick}
      // @TODO: add aria-labelledby & aria-describedby
    >
      <div className={styles['modal-dialog']} role='document'>
        <div
          ref={contentRef}
          className={cx([
            styles['modal-content'],
            width && styles[`modal-content--width-${width}`],
            hasBorder && styles[`modal-content--has-border`],
            hasBackgroundVideo && styles['modal-content--has-bg-video'],
            hasBackgroundVideo && styles[`modal-content--no-padding`],
            `has-bg-color-${backgroundColor}`
          ])}
        >
          <div className={styles['modal-content__inner']}>
            <div className={styles['close-container']}>
              <button className={styles['close-button']} onClick={handleModalClose} data-testid='modal-close-icon'>
                <CloseThick />
              </button>
            </div>
            {children || getView()}
          </div>
          {backgroundImage?.url && (
            <Image
              src={backgroundImage.url}
              alt={backgroundImage?.altText || ''}
              fill
              style={{ objectFit: 'cover' }}
              quality={NEXT_IMAGE_QUALITY}
            />
          )}
        </div>
      </div>
    </div>
  ) : null

  const portalRoot = getPortalRoot()
  if (!portalRoot) return null

  return ReactDOM.createPortal(modalContent, portalRoot)
}

export default Modal
